// src/getSuggestedCategory.js
import suggestionRules from "./suggestionRules";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "./firebase";
import { fetchUserRules } from "./ruleMatcher"; // adjust to your structure
import { PFCV2_DETAILED_TO_TRELLI } from "./pfcToTrelliMap";

// Coerce any value into a string label
const toLabel = (v) => {
  if (typeof v === "string") return v;
  if (v == null) return "";
  if (typeof v === "object") {
    if (typeof v.name === "string") return v.name;
    if (typeof v.label === "string") return v.label;
    if (typeof v.value === "string") return v.value;
    try { return String(v); } catch { return ""; }
  }
  return String(v);
};

// Ensure budgets passed to the matcher have string category/subcategory names
const normalizeBudgetsForMatching = (list = []) =>
  list.map((b) => {
    const category = toLabel(b?.category ?? "");
    const subcategories = (b?.subcategories || []).map((s) => {
      const name = toLabel(typeof s === "string" ? s : (s?.name ?? s));
      // keep any other fields on sub (amount, etc.), but force name to string
      return typeof s === "object" ? { ...s, name } : { name };
    });
    return { ...b, category, subcategories };
  });

// --- PFC helpers (v2-friendly) ---
const normStr = (s) => (s || "").toString().trim().toLowerCase();

const tokensFromPfc = (s) =>
  normStr(s)
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean);

// Find a valid {category, subcategory} from the user's budgets using fuzzy token overlap
function resolveToValidPair(validPairs, targetCategory, targetSubcategory, extraTokens = []) {
  const cat = normStr(targetCategory);
  const sub = normStr(targetSubcategory);

  // 1) Exact subcategory match
  if (sub) {
    const exactSub = validPairs.find((p) => normStr(p.subcategory) === sub);
    if (exactSub) return exactSub;
  }

  // 2) Exact category + subcategory contains
  if (cat && sub) {
    const containsSub = validPairs.find(
      (p) => normStr(p.category) === cat && normStr(p.subcategory).includes(sub)
    );
    if (containsSub) return containsSub;
  }

  // 3) Token overlap scoring across all valid pairs
  const wanted = new Set([...tokensFromPfc(targetCategory), ...tokensFromPfc(targetSubcategory), ...extraTokens]);
  if (!wanted.size) return null;

  let best = null;
  let bestScore = 0;

  for (const p of validPairs) {
    const hay = new Set([
      ...tokensFromPfc(p.category),
      ...tokensFromPfc(p.subcategory),
    ]);

    let score = 0;
    wanted.forEach((t) => {
      if (t.length >= 3 && hay.has(t)) score += 2;     // strong hit
      else if (t.length >= 4) {
        // partial containment boosts (fuel ~ fueling, etc.)
        const joined = `${normStr(p.category)} ${normStr(p.subcategory)}`;
        if (joined.includes(t)) score += 1;
      }
    });

    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }

  return bestScore >= 2 ? best : null; // minimum confidence threshold
}

// --- PFCv2 detailed -> Trelli "intent" mapping (starter set) ---
// You can grow this list over time; it covers most household spend patterns.
// --- Plaid PFCv2 taxonomy mapping (single source of truth) ---
// We map Plaid's personal_finance_category.detailed (PFCv2 Detailed) directly to Trelli defaults.
// If we don't find a mapping, we fall back to:
//   - Expense: Personal & Lifestyle → General Shopping
//   - Income:  Income → Gifts Received
const FALLBACK_EXPENSE = { category: "Personal & Lifestyle", subcategory: "General Shopping" };
const FALLBACK_INCOME = { category: "Income", subcategory: "Gifts Received" };

function pfcIntentFromTransaction(transaction) {
  const detailed =
    transaction?.personal_finance_category?.detailed ||
    transaction?.categoryDetailed ||
    null;

  const primary =
    transaction?.personal_finance_category?.primary ||
    transaction?.categoryPrimary ||
    null;

  // --- Smart overrides for PFCv2 edge cases (no rules/pseudoRules matched) ---
  const vendor = normStr(transaction?.vendor);
  const paymentChannel = normStr(transaction?.paymentChannel);
  const transactionType = normStr(transaction?.transactionType);
  const isTransferFlag = !!transaction?.isTransfer;

  // 0) Vendor is a known Life Insurance carrier → suggest Life Insurance
  if (
    vendor.includes("massmutual") ||
    vendor.includes("northwestern mutual") ||
    vendor.includes("new york life") ||
    vendor.includes("guardian life") ||
    vendor.includes("prudential") ||
    vendor.includes("metlife")
  ) {
    return { category: "Health & Insurance", subcategory: "Life Insurance", detailed, primary };
  }

  // 1) Zelle (or similar P2P) "transfer in" that is NOT marked as transfer → likely Gift/Income
  // Example: "Zelle: B Blair Jones" came in as TRANSFER_IN_ACCOUNT_TRANSFER but isTransfer=false.
  if (
    (vendor.includes("zelle") || vendor.includes("venmo") || vendor.includes("cash app") || vendor.includes("cashapp")) &&
    primary === "TRANSFER_IN" &&
    !isTransferFlag
  ) {
    return { category: "Income", subcategory: "Gifts Received", detailed, primary };
  }

  // 2) NEVER suggest Internal Transfers for TRANSFER_OUT account transfers unless it's truly confirmed.
  // If PFC says TRANSFER_OUT_* but isTransfer=false, we treat it as unknown and let heuristics/fallback handle it.
  if (
    primary === "TRANSFER_OUT" &&
    !isTransferFlag &&
    (
      // any account-transfer flavored detail should NOT default to internal transfer
      (detailed && detailed.includes("ACCOUNT_TRANSFER")) ||
      // and also treat common P2P rails as "unknown spend" unless explicitly marked transfer
      vendor.includes("venmo") ||
      vendor.includes("zelle") ||
      vendor.includes("paypal") ||
      vendor.includes("cash app") ||
      vendor.includes("cashapp")
    )
  ) {
    return { category: null, subcategory: null, detailed, primary };
  }

  // 2b) PFC says TRANSFER_OUT but metadata looks like a normal purchase → don't force Internal Transfers
  if (
    primary === "TRANSFER_OUT" &&
    !isTransferFlag &&
    (paymentChannel.includes("in store") || transactionType === "place")
  ) {
    return { category: null, subcategory: null, detailed, primary };
  }

  // 3) RENT_AND_UTILITIES disambiguation (esp. OTHER_UTILITIES)
  // Plaid v2 sometimes dumps real utilities into OTHER_UTILITIES.
  // Use vendor hints to choose the right Utilities subcategory.
  const isRentAndUtils =
    primary === "RENT_AND_UTILITIES" ||
    (typeof detailed === "string" && detailed.startsWith("RENT_AND_UTILITIES_"));

  if (isRentAndUtils) {
    // Helper: simple “contains any” matcher
    const has = (arr) => arr.some((k) => vendor.includes(k));

    // Gas / propane / delivered fuel (utility gas) — NOT auto fuel
    if (has(["propane", "lp gas", "lpg", "natural gas", "gas co", "gas company", "fuel company", "heating fuel"])) {
      return { category: "Utilities", subcategory: "Gas", detailed, primary };
    }

    // Electricity
    if (has(["electric", "electricity", "power", "energy", "georgia power", "duke energy", "emc", "light bill"])) {
      return { category: "Utilities", subcategory: "Electricity", detailed, primary };
    }

    // Water & Sewer
    if (has(["water", "sewer", "wastewater", "stormwater", "watershed"])) {
      return { category: "Utilities", subcategory: "Water & Sewer", detailed, primary };
    }

    // Trash / Recycling
    if (has(["trash", "garbage", "waste management", "republic services", "recycling", "sanitation"])) {
      return { category: "Utilities", subcategory: "Trash / Recycling", detailed, primary };
    }

    // Internet
    if (has(["internet", "wifi", "wi-fi", "broadband", "fiber", "xfinity", "comcast", "spectrum", "charter", "cox", "google fiber"])) {
      return { category: "Utilities", subcategory: "Internet", detailed, primary };
    }

    // Phone
    if (has(["phone", "wireless", "cell", "verizon", "at&t", "att", "tmobile", "t-mobile", "sprint"])) {
      return { category: "Utilities", subcategory: "Phone", detailed, primary };
    }
  }
  
  if (detailed && PFCV2_DETAILED_TO_TRELLI[detailed]) {
    const mapped = PFCV2_DETAILED_TO_TRELLI[detailed];
    if (mapped?.category && mapped?.subcategory) {
      return { ...mapped, detailed, primary };
    }
  }

  const amt = Number(transaction?.amount || 0);
  const isIncome = amt > 0;

  return {
    ...(isIncome ? FALLBACK_INCOME : FALLBACK_EXPENSE),
    detailed,
    primary,
  };
}



export async function getSuggestedCategory(transaction, budgets) {
  const safeBudgets = normalizeBudgetsForMatching(budgets || []);
  const name = normStr(transaction.vendor);
  const bankCategory = normStr(transaction.bankCategory);

  // Prefer PFC v2 fields (now stored correctly on the root transaction doc)
  const pfcDetailed = normStr(transaction.categoryDetailed);
  const pfcPrimary = normStr(transaction.categoryPrimary);

  // Build a normalized token list we can use in fallback matching
  const pfcTokens = [
    ...tokensFromPfc(pfcDetailed),
    ...tokensFromPfc(pfcPrimary),
  ];

  // Keep legacy/old fields for fallback (some data may still have them)
  const legacyPlaidCategories = Array.isArray(transaction.category)
    ? transaction.category.map((c) => normStr(c))
    : transaction.category
      ? [normStr(transaction.category)]
      : [];

  // For your existing synonym logic, use a unified list of “category strings”
  // that includes PFC tokens + legacy strings.
  const plaidCategories = [
    pfcDetailed,
    pfcPrimary,
    ...legacyPlaidCategories,
    ...pfcTokens,
  ].filter(Boolean);

  const validPairs = safeBudgets.flatMap((b) =>
    (b.subcategories || [])
      // keep anything that has a displayable name (i.e., visible in your UI)
      .filter((sub) => toLabel(sub?.name).trim() !== "")
      .map((sub) => ({ category: b.category, subcategory: toLabel(sub.name) }))
  );

  // 🟢 0) Try to suggest based on real rules (loose vendor match)
  const userRules = await fetchUserRules(); // assume cached upstream if needed

  if (userRules && userRules.length > 0) {
    const vendor = (transaction.vendor || "").toLowerCase();
    const amount = Number(transaction.amount);
    const plaidCategory = Array.isArray(transaction.category)
      ? (transaction.category[0] || "")
      : (transaction.bankCategory || "");

    // 0a) Exact vendor + exact amount + exact plaidCategory
    let ruleMatch = userRules.find(r =>
      (r.vendor || "").toLowerCase() === vendor &&
      Number(r.amount) === amount &&
      (r.plaidCategory || "") === plaidCategory
    );

    // 0b) Vendor + amount within 20% + exact plaidCategory
    if (!ruleMatch) {
      ruleMatch = userRules.find(r =>
        (r.vendor || "").toLowerCase() === vendor &&
        Math.abs(Number(r.amount) - amount) / Math.abs(Number(r.amount) || 1) <= 0.2 &&
        (r.plaidCategory || "") === plaidCategory
      );
    }

    // 0c) Vendor soft match only (prefix/contains)
    if (!ruleMatch) {
      ruleMatch = userRules.find(r => {
        const rv = (r.vendor || "").toLowerCase();
        return rv &&
          (rv.startsWith(vendor) || vendor.startsWith(rv) || rv.includes(vendor) || vendor.includes(rv));
      });
    }

    if (ruleMatch) {
      return {
        category: toLabel(ruleMatch.category),
        subcategory: toLabel(ruleMatch.subcategory),
        source: "rule"
      };
    }
  }

  // 🟢 1) Check pseudo rules first
  const user = auth.currentUser;
  if (user) {
    const pseudoQuery = query(
      collection(db, "pseudoRules"),
      where("userId", "==", user.uid)
    );
    const snapshot = await getDocs(pseudoQuery);
    const pseudoRules = snapshot.docs.map((doc) => doc.data());

    const vendor = (transaction.vendor || "").toLowerCase();
    const amount = Number(transaction.amount);
    const plaidCategory = Array.isArray(transaction.category) ? transaction.category[0] : transaction.bankCategory || "";

    // 1) Vendor + amount + plaidCategory exact
    let match = pseudoRules.find(r =>
      r.vendor?.toLowerCase() === vendor &&
      Number(r.amount) === amount &&
      r.plaidCategory === plaidCategory
    );

    // 2) Vendor + amount +/- 20% + plaidCategory
    if (!match) {
      match = pseudoRules.find(r =>
        r.vendor?.toLowerCase() === vendor &&
        Math.abs(Number(r.amount) - amount) / Math.abs(Number(r.amount)) <= 0.2 &&
        r.plaidCategory === plaidCategory
      );
    }

    // 3) Vendor + amount +/- 20% + plaidCategory whole-word overlap
    if (!match) {
      match = pseudoRules.find(r => {
        if (
          r.vendor?.toLowerCase() !== vendor ||
          Math.abs(Number(r.amount) - amount) / Math.abs(Number(r.amount)) > 0.2
        ) {
          return false;
        }

        const ruleWords = r.plaidCategory.split(/\s+/).map(w => w.toLowerCase());
        const txWords = plaidCategory.split(/\s+/).map(w => w.toLowerCase());

        return ruleWords.some(word => txWords.includes(word));
      });
    }

    // 4) Vendor + plaidCategory
    if (!match) {
      match = pseudoRules.find(r =>
        r.vendor?.toLowerCase() === vendor &&
        r.plaidCategory === plaidCategory
      );
    }

    // 5) Vendor only
    if (!match) {
      match = pseudoRules.find(r =>
        r.vendor?.toLowerCase() === vendor
      );
    }

    if (match) {
      return {
        category: match.category,
        subcategory: match.subcategory,
        source: "pseudoRule"
      };
    }
  }

  // 🟣 2) PFCv2-first suggested match (only if no rule / pseudoRule matched)
  // Translate Plaid PFC detailed/primary into a Trelli intent, then resolve to the user's actual budgets.
  const intent = pfcIntentFromTransaction(transaction);

  if (intent?.category && intent?.subcategory) {
    const resolved = resolveToValidPair(
      validPairs,
      intent.category,
      intent.subcategory,
      // Extra tokens can help match user-custom names ("Auto Fuel", "Gasoline", etc.)
      [...tokensFromPfc(intent.primary), ...tokensFromPfc(intent.detailed), ...tokensFromPfc(name)]
    );

    if (resolved) {
      return { ...resolved, source: "suggested-pfc" };
    }
  }

  let bestMatch = null;

  // 1️⃣ Strong match: Plaid/Bank + Vendor in same subcategory
  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    const vendorMatch = synonyms.some((syn) => name.includes(syn));
    const plaidOrBankMatch =
      plaidCategories.some((p) => synonyms.some((syn) => p.includes(syn))) ||
      synonyms.some((syn) => bankCategory.includes(syn));
    if (vendorMatch && plaidOrBankMatch) {
      const match = validPairs.find(
        (pair) =>
          pair.subcategory.toLowerCase() === subName.toLowerCase() ||
          pair.category.toLowerCase() === subName.toLowerCase()
      );
      if (match) {
        return { ...match, source: "suggested" };
      }
    }
  }

  // New: One FULL + One PARTIAL combo
  let maxComboCount = 0;
  let bestComboSub = null;

  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    let hasFullPlaidBank = false;
    let hasPartialVendor = false;

    plaidCategories.forEach((p) => {
      synonyms.forEach((syn) => {
        const regex = new RegExp(`\\b${syn}\\b`, 'i');
        if (regex.test(p)) hasFullPlaidBank = true;
        else if (syn.length >= 3 && p.includes(syn)) hasPartialVendor = true;
      });
    });

    if (bankCategory) {
      synonyms.forEach((syn) => {
        const regex = new RegExp(`\\b${syn}\\b`, 'i');
        if (regex.test(bankCategory)) hasFullPlaidBank = true;
        else if (syn.length >= 3 && bankCategory.includes(syn)) hasPartialVendor = true;
      });
    }

    synonyms.forEach((syn) => {
      const regex = new RegExp(`\\b${syn}\\b`, 'i');
      if (regex.test(name)) {
        if (hasFullPlaidBank) hasPartialVendor = true;
      } else if (syn.length >= 3 && name.includes(syn)) {
        if (hasFullPlaidBank) hasPartialVendor = true;
      }
    });

    // Reverse: Vendor FULL + Plaid/BANK PARTIAL
    let hasFullVendor = false;
    let hasPartialPlaidBank = false;

    synonyms.forEach((syn) => {
      const regex = new RegExp(`\\b${syn}\\b`, 'i');
      if (regex.test(name)) hasFullVendor = true;
    });

    plaidCategories.forEach((p) => {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && p.includes(syn)) hasPartialPlaidBank = true;
      });
    });

    if (bankCategory) {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && bankCategory.includes(syn)) hasPartialPlaidBank = true;
      });
    }

    const comboCount =
      (hasFullPlaidBank && hasPartialVendor) || (hasFullVendor && hasPartialPlaidBank)
        ? 1
        : 0;

    if (comboCount > maxComboCount) {
      maxComboCount = comboCount;
      bestComboSub = subName;
    }
  }

  if (bestComboSub && maxComboCount > 0) {
    const match = validPairs.find(
      (pair) =>
        pair.subcategory.toLowerCase() === bestComboSub.toLowerCase() ||
        pair.category.toLowerCase() === bestComboSub.toLowerCase()
    );
    if (match) {
      return { ...match, source: "suggested" };
    }
  }

  // 🔍 Plaid/Bank + Vendor PARTIAL + PARTIAL combo
  let maxPartialComboCount = 0;
  let bestPartialComboSub = null;

  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    let hasPartialPlaidBank = false;
    let hasPartialVendor = false;

    // Check plaid categories for partials
    plaidCategories.forEach((p) => {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && p.includes(syn)) {
          hasPartialPlaidBank = true;
        }
      });
    });

    // Check bank category for partials
    if (bankCategory) {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && bankCategory.includes(syn)) {
          hasPartialPlaidBank = true;
        }
      });
    }

    // Check vendor name for partials
    synonyms.forEach((syn) => {
      if (syn.length >= 3 && name.includes(syn)) {
        hasPartialVendor = true;
      }
    });

    if (hasPartialPlaidBank && hasPartialVendor) {
      // Count it once — you could add weighting if you want to favor more matches
      maxPartialComboCount = 1;
      bestPartialComboSub = subName;
      break; // Optional: break if you want first found, or let it run to find multiple
    }
  }

  if (bestPartialComboSub && maxPartialComboCount > 0) {
    const match = validPairs.find(
      (pair) =>
        pair.subcategory.toLowerCase() === bestPartialComboSub.toLowerCase() ||
        pair.category.toLowerCase() === bestPartialComboSub.toLowerCase()
    );
    if (match) {
      return { ...match, source: "suggested" };
    }
  }

  // 2️⃣ Plaid/Bank WHOLE WORD match — find subcategory with most full-word hits
  let maxWholeWordCount = 0;
  let bestWholeWordSub = null;

  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    let count = 0;

    plaidCategories.forEach((p) => {
      synonyms.forEach((syn) => {
        const regex = new RegExp(`\\b${syn}\\b`, 'i');
        if (regex.test(p)) count++;
      });
    });

    if (bankCategory) {
      synonyms.forEach((syn) => {
        const regex = new RegExp(`\\b${syn}\\b`, 'i');
        if (regex.test(bankCategory)) count++;
      });
    }

    if (count > maxWholeWordCount) {
      maxWholeWordCount = count;
      bestWholeWordSub = subName;
    }
  }

  if (bestWholeWordSub && maxWholeWordCount > 0) {
    const match = validPairs.find(
      pair =>
        pair.subcategory.toLowerCase() === bestWholeWordSub.toLowerCase() ||
        pair.category.toLowerCase() === bestWholeWordSub.toLowerCase()
    );
    if (match) {
      return { ...match, source: "suggested" };
    }
  }

  // 4️⃣ Vendor WHOLE WORD match
  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    for (const syn of synonyms) {
      const regex = new RegExp(`\\b${syn}\\b`, 'i'); // whole word match
      if (regex.test(name)) {
        const match = validPairs.find(
          pair =>
            pair.subcategory.toLowerCase() === subName.toLowerCase() ||
            pair.category.toLowerCase() === subName.toLowerCase()
        );
        if (match) {
          return { ...match, source: 'suggested' };
        }
      }
    }
  }

  // 3️⃣ Plaid/Bank synonyms ONLY — find subcategory with most Plaid/bank matches
  let maxCount = 0;
  let bestSub = null;

  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    let count = 0;

    // Check ONLY plaid categories
    plaidCategories.forEach((p) => {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && p.includes(syn)) count++;
      });
    });

    // Also check bank category
    if (bankCategory) {
      synonyms.forEach((syn) => {
        if (syn.length >= 3 && bankCategory.includes(syn)) count++;
      });
    }

    if (count > maxCount) {
      maxCount = count;
      bestSub = subName;
    }
  }

  if (bestSub && maxCount > 0) {
    const match = validPairs.find(
      (pair) =>
        pair.subcategory.toLowerCase() === bestSub.toLowerCase() ||
        pair.category.toLowerCase() === bestSub.toLowerCase()
    );
    if (match) {
      return { ...match, source: "suggested" };
    }
  }

  // 5️⃣ Vendor PARTIAL match
  for (const [subName, synonyms] of Object.entries(suggestionRules)) {
    const isTransferFlag = !!transaction?.isTransfer;
    const paymentChannel = normStr(transaction?.paymentChannel);
    const txType = normStr(transaction?.transactionType);

    const looksLikeInStorePurchase =
      !isTransferFlag && paymentChannel.includes("in store") && txType === "place";

    const s = (subName || "").toLowerCase();
    if (looksLikeInStorePurchase && s === "internal transfers") continue;
    if (looksLikeInStorePurchase && s === "credit card in same month") continue;

    if (
      synonyms.some((syn) => syn.length >= 3 && name.includes(syn))
    ) {
      const match = validPairs.find(
        (pair) =>
          pair.subcategory.toLowerCase() === subName.toLowerCase() ||
          pair.category.toLowerCase() === subName.toLowerCase()
      );
      if (match) {
        return { ...match, source: 'suggested' };
      }
    }
  }

  // 6 Fallbacks
  if (transaction.amount < 0) {
    return { category: "Personal & Lifestyle", subcategory: "General Shopping", source: "suggested" };
  }
  if (transaction.amount > 0) {
    return { category: "Income", subcategory: "Gifts Received", source: "suggested" };
  }

  console.log("❌ No suggestion found for:", transaction);
  return null;
}